import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/services/auth/auth.service';
import { GainsUiCommonsModule } from '@gainsystems/gains-ui-commons';
import { GainsTenantSwitcherComponent } from 'app/shared/modules/gains-tenant-switcher/gains-tenant-switcher.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Tenant, TenantService, TenantsResponse } from 'app/services/tenant/tenant.service';
import { FeatureFlagsService } from 'app/shared/services/feature-flags/feature-flags.service';

@Component({
  imports: [CommonModule, GainsUiCommonsModule],
  standalone: true,
  templateUrl: './auth-callback.component.html',
  styleUrl: './auth-callback.component.scss'
})
export class AuthCallbackComponent implements OnInit {
  isLoadingTenantSwitcher = true;

  constructor(
    private readonly _authService: AuthService,
    private readonly _modalService: NzModalService,
    private readonly _tenantService: TenantService,
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _router: Router
  ) {}

  async ngOnInit() {
    // Load-bearing, do not remove: /auth-callback has no route guards, so blocking initial
    // navigation settles this route without waiting on auth. This await is the only thing
    // keeping loadTenants() from firing before Keycloak has exchanged the code.
    await this._authService.ready;

    // check-sso also comes back here when there is no Keycloak session (error=login_required).
    if (!this._authService.isLoggedIn()) {
      await this._router.navigateByUrl('/login', { replaceUrl: true });
      return;
    }

    const tenantsResponse = await this.loadTenantsOrNull();

    // Only tenant loading falls back to the token: once a tenant is settled, a later failure must
    // not silently replace the user's choice and persist that instead.
    if (!tenantsResponse) {
      await this.useDefaultTenantFromTokenAndRedirect();
      return;
    }

    if (tenantsResponse.tenants.length > 1) {
      await this.selectTenantOrShowSwitcher(tenantsResponse);
    } else if (tenantsResponse.tenants.length === 0) {
      await this.useDefaultTenantFromTokenAndRedirect();
    } else {
      this._tenantService.setSelectedTenant(tenantsResponse.tenants[0].tenantId, tenantsResponse.tenants[0].customerName);
      await this.loadFeatureFlagsAndRedirect();
    }
  }

  private async loadTenantsOrNull(): Promise<TenantsResponse | null> {
    try {
      return await this._tenantService.loadTenants();
    } catch {
      return null;
    }
  }

  /**
   * Keycloak inits with onLoad: 'check-sso', so every page load with a live Keycloak session — a
   * plain refresh included — re-enters this route. Reusing the tenant chosen earlier in the session is what stops the
   * switcher from reappearing on every refresh; a fresh login has none, so it still asks.
   */
  private async selectTenantOrShowSwitcher(tenantsResponse: TenantsResponse): Promise<void> {
    const alreadySelectedTenant = this.findAlreadySelectedTenant(tenantsResponse);

    if (!alreadySelectedTenant) {
      this.isLoadingTenantSwitcher = false;
      this.showTenantSwitcherModal(tenantsResponse);
      return;
    }

    this._tenantService.setSelectedTenant(alreadySelectedTenant.tenantId, alreadySelectedTenant.customerName);
    await this.loadFeatureFlagsAndRedirect();
  }

  /** Matched against the fetched list so a tenant the user has lost access to falls back to the switcher. */
  private findAlreadySelectedTenant(tenantsResponse: TenantsResponse): Tenant | null {
    const selectedTenantId = this._tenantService.getTenantSelectedThisSession();
    if (!selectedTenantId) {
      return null;
    }

    return tenantsResponse.tenants.find(tenant => tenant.tenantId === selectedTenantId) ?? null;
  }

  private async useDefaultTenantFromTokenAndRedirect(): Promise<void> {
    const tenantFromToken = this._authService.getTenantFromToken();
    if (tenantFromToken) {
      this._tenantService.setSelectedTenant(tenantFromToken);
      await this.loadFeatureFlagsAndRedirect();
    }
  }

  /**
   * Feature flags are tenant-scoped, so they must be fetched only once the tenant is settled —
   * never before the switcher resolves. Loading them here also means the destination route's
   * FeatureGuard finds a warm cache instead of issuing a second request.
   */
  private async loadFeatureFlagsAndRedirect(): Promise<void> {
    await this._featureFlagsService.ensureFeatureFlagsLoaded();
    await this._authService.handleRedirectAfterLogin();
  }

  private showTenantSwitcherModal(tenantsResponse: TenantsResponse): void {
    const modal = this._modalService.create({
      nzContent: GainsTenantSwitcherComponent,
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: false,
      nzWidth: 500,
      nzBodyStyle: { padding: '0' },
      nzData: {
        tenantsResponse
      }
    });
    modal.afterClose.subscribe(async selectedTenant => {
      if (selectedTenant) {
        const selectedTenantData = tenantsResponse.tenants.find(tenant => tenant.tenantId === selectedTenant.value);
        this._tenantService.setSelectedTenant(selectedTenant.value, selectedTenantData?.customerName ?? null);
        await this.loadFeatureFlagsAndRedirect();
      } else {
        this._authService.logout();
      }
    });
  }
}
